import React, { FC } from 'react';
import { connect } from 'react-redux';
import Loading, { SmallLoading } from '@components/loading';
import { productionBatchData } from '@reducers/statistics';
import {
  getProductionBatchByMachineId,
  getProductionBatchByNCFile,
  productionBatchesSearch,
  productionBatchesSuggestion,
} from '../../../../../../reducers/search';
import { fullWidth, scale } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { Animated, View } from 'react-native';
import { setAllowLoadMoreData, setDisplayScrollViewLoader, setLoadDataDisabled } from '@reducers/machinesV2';
import { setBatchList, setBatchListContainerHeight, setBatchListWidth, setBatchListYPosition } from '@reducers/batch';
import {
  LIST_WIDTH,
  SCREEN_PADDING_HORIZONTAL,
  SCREEN_PADDING_HORIZONTAL_FOR_ITEM,
  SCREEN_PADDING_VERTICAL,
  SCREEN_PADDING_VERTICAL_FOR_ITEM,
  TABLE_NAMES,
  TEST_IDS,
} from '../../constants/base.constants';
import { SearchInputWithModeSelector } from '@components/search/components/SearchInputWithModeSelector';
import { isPhone, isTabletOrWeb, LEVELS, searchDisplayMode } from '@helpers';
import Suggestion from '@components/search/components/suggestion';
import { setSelectedNCFile } from '@reducers/machines';
import Statistic from '@components/search/components/statistic';
import ProductionBatches from '@components/search/productionBatches';
import { generateImageURL } from '@reducers/blobStorage';
import NoResult from '@components/search/components/noResult';
import MobileHeader from '@components/search/components/v2/MobileHeader';
import { OwnProps, DispatchProps } from './types';
import { useManageBatchListContent } from './hooks/useManageBatchListContent';

// Re-export for external consumers
export type { GetBatchListParams } from './types';

const BatchListContent: FC<OwnProps & DispatchProps> = (props) => {
  const {
    C,
    batchList,
    batchListWidth,
    setBatchListWidth,
    setSelectedNCFile,
    allowLoadMoreData,
    setAllowLoadMoreData,
    noSearchScreen,
    backgroundColorForTile,
    withoutImage,
  } = props;

  const {
    batchLoading,
    totalCount,
    searchValue,
    suggestions,
    facets,
    isError,
    displayMode,
    searchWasTriggered,
    searchTileName,
    statisticTileName,
    setStatisticTileName,
    setContentHeight,
    columnVisibilityModel,
    setColumnVisibilityModel,
    filter,
    setFilter,
    filterModel,
    setFilterModel,
    orderBy,
    setOrderBy,
    sortModel,
    setSortModel,
    oDataFilter,
    setODataFilter,
    usedODataFilter,
    customAnchorRef,
    apiRef,
    fadeAnim,
    searchRef,
    batchListOffset,
    batchListInfoTileModeOffset,
    top,
    isMachinesScreen,
    IS_NARROW_WEB,
    isSmallMode,
    isLineMode,
    isTileMode,
    isTableMode,
    marginTop,
    scrollViewNeeded,
    setDisplayMode,
    setSuggestions,
    onSearch,
    cancelAnimation,
    submit,
    searchBatchesWithTile,
    scrollListener,
    loadBatchList,
    setBatchListContainerHeight,
  } = useManageBatchListContent(props);

  const Content = (
    <>
      {!batchList.length && searchWasTriggered && !isTableMode && (
        <NoResult {...{ C, value: searchValue || searchTileName, style: { marginTop: isPhone ? scale(100) : 0, flex: 1 } }} />
      )}
      {(!!batchList.length || isTableMode) && (
        <ProductionBatches
          {...{
            totalSearchResult: totalCount,
            searchResult: isTileMode && !!batchList.length ? batchList.slice(0, batchListInfoTileModeOffset.current + top) : batchList,
            isError,
            loaded: true,
            C,
            isSmallMode,
            isLineMode,
            isInfoTilesMode: isTileMode,
            isTableMode,
            fetchData: loadBatchList,
            generateImageURL,
            recentlyViewed: [],
            setRecentlyViewed: () => {},
            setSelectedNCFile,
            facets,
            containerWidth:
              isTileMode && (!isPhone || !isMachinesScreen)
                ? batchListWidth - 4
                : isPhone && !isMachinesScreen && !IS_NARROW_WEB
                ? batchListWidth + SCREEN_PADDING_HORIZONTAL * 2
                : IS_NARROW_WEB && !isMachinesScreen
                ? batchListWidth + SCREEN_PADDING_HORIZONTAL_FOR_ITEM * 2
                : batchListWidth,
            withoutLoader: true,
            withoutHorizontalPaddings: IS_NARROW_WEB ? !isMachinesScreen : isTabletOrWeb || !isMachinesScreen,
            apiRef,
            marginTop,
            setOrderBy,
            orderBy,
            sortModel,
            setSortModel,
            columnVisibilityModel,
            setColumnVisibilityModel,
            filter,
            setFilter,
            filterModel,
            setFilterModel,
            oDataFilter,
            setODataFilter,
            search: ({ orderBy, oDataFilter: filterOData }: { orderBy?: string[]; oDataFilter?: string | null }) => {
              if (filterOData !== undefined) {
                usedODataFilter.current = filterOData || '';
              }
              return loadBatchList(0, searchValue, orderBy ? { orderBy } : undefined);
            },
            onRowsScrollEnd: () => loadBatchList(),
            bg: isPhone ? C('Background/Light/System') : C('Background/Light/Detail'),
            noSearchScreen: true,
            backgroundColorForTile,
            withoutImage,
            tableName: TABLE_NAMES.batchList,
          }}
        />
      )}
    </>
  );

  const ContentWithWrapper = (
    <View
      style={[
        {
          width: '100%',
          flex: 1,
        },
      ]}
    >
      {(batchLoading && batchListOffset.current === 0) || !displayMode ? (
        <View style={{ marginTop: scale(40) }}>
          <Loading background={'transparent'} />
        </View>
      ) : (
        <>
          {!scrollViewNeeded ? (
            Content
          ) : (
            <Animated.ScrollView
              testID={TEST_IDS.historyScreen.batchListScrollView}
              onLayout={(event) => {
                const { height } = event.nativeEvent.layout;
                setBatchListContainerHeight(height);
              }}
              onContentSizeChange={(width, height) => {
                setContentHeight(height);
              }}
              onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: fadeAnim } } }], { listener: scrollListener, useNativeDriver: false })}
              contentContainerStyle={[
                {
                  minHeight: '100%',
                  paddingVertical: scale(SCREEN_PADDING_VERTICAL_FOR_ITEM),
                  paddingHorizontal: scale(isMachinesScreen ? SCREEN_PADDING_HORIZONTAL_FOR_ITEM : 0),
                },
              ]}
              showsVerticalScrollIndicator={false}
            >
              {Content}
            </Animated.ScrollView>
          )}
        </>
      )}
    </View>
  );

  const StatisticComponent = (
    <Statistic
      {...{
        C,
        level: LEVELS.ProductionBatches,
        oDataFilter: null,
        searchValue,
        isSearchTriggered: searchWasTriggered,
        statisticTileName,
        setStatisticTileName,
        search: searchBatchesWithTile,
        facets,
        loaded: !batchLoading,
        style: {
          paddingHorizontal: scale(
            isPhone && isMachinesScreen ? SCREEN_PADDING_HORIZONTAL : isPhone && !isMachinesScreen ? 0 : SCREEN_PADDING_VERTICAL_FOR_ITEM
          ),
          width: IS_NARROW_WEB
            ? isMachinesScreen
              ? batchListWidth - SCREEN_PADDING_HORIZONTAL * 2
              : batchListWidth
            : batchListWidth
            ? batchListWidth - SCREEN_PADDING_VERTICAL_FOR_ITEM
            : fullWidth - LIST_WIDTH - SCREEN_PADDING_VERTICAL_FOR_ITEM - SCREEN_PADDING_HORIZONTAL * 1.7,
        },
        containerMarginBottom: displayMode === searchDisplayMode.infoTiles ? 0 : scale(18),
      }}
    />
  );

  return (
    <View
      onLayout={(event) => {
        setBatchListWidth(event.nativeEvent.layout.width);
      }}
      style={{ flex: 1, width: '100%', alignItems: 'center' }}
    >
      {isPhone && !IS_NARROW_WEB && (
        <MobileHeader
          {...{
            displayMode,
            setSelectedDisplayMode: setDisplayMode,
            title: '',
            suggestion: suggestions,
            C,
            onSearch,
            setSuggestion: setSuggestions,
            searchRef,
            cancelAnimation,
            setSelectedNCFile,
            renderProps: {},
            searchValue,
            submit,
            loaded: !batchLoading,
            isError,
            totalSearchResult: totalCount,
            isAllLevel: false,
            alowLoadMore: allowLoadMoreData,
            setLoadMore: setAllowLoadMoreData,
            loadMore: loadBatchList,
            level: LEVELS.ProductionBatches,
            noSearchScreen,
            isMachinesScreen,
            width: isPhone && !isMachinesScreen ? batchListWidth + SCREEN_PADDING_HORIZONTAL : batchListWidth,
            scrollListener,
          }}
        >
          <View style={{ marginTop: scale(SCREEN_PADDING_VERTICAL) }}>
            {StatisticComponent}
            {(batchLoading && batchListOffset.current === 0) || !displayMode ? (
              <View style={{ marginTop: scale(40) }}>
                <SmallLoading />
              </View>
            ) : (
              Content
            )}
          </View>
        </MobileHeader>
      )}

      {isTabletOrWeb && (
        <View
          style={[
            {
              flex: 1,
              width: '100%',
            },
          ]}
        >
          <SearchInputWithModeSelector
            {...{
              C,
              cancelAnimation,
              displayMode: displayMode ?? searchDisplayMode.infoTiles,
              inputName: '',
              level: LEVELS.ProductionBatches,
              onSearch,
              searchRef,
              searchValue,
              setSelectedDisplayMode: setDisplayMode,
              showModeSelector: true,
              submit,
              searchInputWithModeSelectorStyle: {
                marginTop: 0,
                marginBottom: scale(18),
                paddingHorizontal: scale(
                  IS_NARROW_WEB && !isMachinesScreen ? 0 : isPhone ? SCREEN_PADDING_HORIZONTAL : SCREEN_PADDING_HORIZONTAL_FOR_ITEM
                ),
              },
              bg: 'Background/Light/Search & View Selector',
              disabledModes: [searchDisplayMode.tableCompact],
            }}
          />
          {StatisticComponent}
          {ContentWithWrapper}
          {isTabletOrWeb && suggestions.length ? (
            <Suggestion
              {...{
                suggestion: suggestions,
                C,
                onSearch,
                setSuggestion: setSuggestions,
                searchRef,
                cancelAnimation,
                setSelectedNCFile,
                width: batchListWidth,
                top: 35,
                tabletAsWeb: true,
              }}
            />
          ) : null}
        </View>
      )}
    </View>
  );
};

export default connect(
  ({
    machinesV2: { allowLoadMoreData, displayScrollViewLoader, loadDataDisabled, selectedGroup },
    machines: { selectedItem },
    batch: { batchList, batchListWidth, batchListContainerHeight },
  }) => ({
    allowLoadMoreData,
    displayScrollViewLoader,
    selectedItem,
    batchList,
    batchListWidth,
    loadDataDisabled,
    batchListContainerHeight,
    selectedGroup,
  }),
  {
    productionBatchData,
    getProductionBatchByNCFile,
    getProductionBatchByMachineId,
    setLoadDataDisabled,
    setDisplayScrollViewLoader,
    setBatchList,
    productionBatchesSearch,
    setBatchListWidth,
    productionBatchesSuggestion,
    setSelectedNCFile,
    generateImageURL,
    setBatchListContainerHeight,
    setBatchListYPosition,
    setAllowLoadMoreData,
  }
)(ColorWrapper(BatchListContent));
